import { motion } from "framer-motion";
import {
  Forklift, Cpu, Factory, Construction, Truck, Ship, Scale, FileCheck2,
  Users, ShieldCheck, ArrowRight,
} from "lucide-react";

// Las 8 líneas "de empresa" primero y el beneficio para colaboradores al final,
// como tarjeta destacada de ancho completo.
const CATEGORIES = [
  {
    id: "maquinaria",
    name: "Maquinaria",
    tag: "Pesada · Móvil · De faena",
    desc:
      "Excavadoras, grúas, cargadores y grúas horquilla: daños físicos, avería de maquinaria y responsabilidad civil de operación.",
    Icon: Forklift,
    image: "/generated/op-prod-maquinaria.jpg",
    footer: "Dentro y fuera de faena",
  },
  {
    id: "equipos",
    name: "Equipos",
    tag: "Industriales · Electrónicos",
    desc:
      "Bombas, compresores, tableros, PLC, servidores e instrumentación. Cubre rotura, cortocircuito, robo y gastos extra de operación.",
    Icon: Cpu,
    image: "/generated/op-prod-equipos.jpg",
    footer: "Rotura + electrónicos",
  },
  {
    id: "plantas",
    name: "Plantas e instalaciones",
    tag: "Edificio · Existencias · Contenido",
    desc:
      "Todo Riesgo Industrial para tu planta, bodega o centro de distribución, con perjuicio por paralización para no perder utilidad.",
    Icon: Factory,
    image: "/generated/op-prod-plantas.jpg",
    footer: "Todo Riesgo + paralización",
  },
  {
    id: "infraestructura",
    name: "Ingeniería y montaje",
    tag: "Obras · Ampliaciones · Proyectos",
    desc:
      "Pólizas CAR y EAR para construcción y montaje: la obra, los equipos, las pruebas y la responsabilidad frente a terceros.",
    Icon: Construction,
    image: "/generated/op-prod-infraestructura.jpg",
    footer: "Construcción y montaje",
  },
  {
    id: "flotas",
    name: "Flotas de vehículos",
    tag: "Camionetas · Camiones · Buses",
    desc:
      "Toda la flota bajo una sola póliza, con administración centralizada, un vencimiento único y tarifas por volumen.",
    Icon: Truck,
    image: "/generated/op-prod-flotas.jpg",
    footer: "Una póliza para toda la flota",
  },
  {
    id: "transporte",
    name: "Transporte de carga",
    tag: "Terrestre · Marítimo · Aéreo",
    desc:
      "Insumos, productos terminados, importaciones y exportaciones protegidos puerta a puerta, en póliza por viaje o flotante anual.",
    Icon: Ship,
    image: "/generated/op-prod-transporte.jpg",
    footer: "Carga propia y de terceros",
  },
  {
    id: "responsabilidad_civil",
    name: "Responsabilidad Civil",
    tag: "General · Patronal · Productos",
    desc:
      "Protege el patrimonio de la empresa ante reclamos de terceros por daños, accidentes o productos, con gastos de defensa incluidos.",
    Icon: Scale,
    image: "/generated/op-prod-rc.jpg",
    footer: "Tu patrimonio a resguardo",
  },
  {
    id: "fiel_cumplimiento",
    name: "Garantías",
    tag: "Fiel cumplimiento · Seriedad de la oferta · Anticipos",
    desc:
      "Fiel cumplimiento, seriedad de la oferta, anticipos y correcta ejecución de las obras, sin inmovilizar tu línea de crédito.",
    Icon: FileCheck2,
    image: "/generated/op-prod-fiel-cumplimiento.jpg",
    footer: "Sin inmovilizar tu línea bancaria",
  },
];

const FEATURED = {
  id: "colaboradores",
  name: "Colaboradores: accidentes personales y autos a precio flota",
  tag: "Beneficio para tu equipo",
  desc:
    "Seguro colectivo de accidentes personales para proteger a tus trabajadores dentro y fuera de la faena, más el seguro de su auto a precio flota con hasta 60% de ahorro, con pólizas individuales y sin costo para la empresa.",
  Icon: Users,
  image: "/generated/op-prod-colaboradores.jpg",
  highlights: [
    "Accidentes personales",
    "Autos a precio flota",
    "Hasta 60% de ahorro",
    "Sin costo para la empresa",
  ],
};

const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const CategoryCard = ({ category, index }) => {
  const Icon = category.Icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`product-card-${category.id}`}
      className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:border-brand-yellow/50 hover:shadow-xl hover:shadow-brand-ink/10"
    >
      <div className="relative overflow-hidden aspect-[2/1] sm:aspect-[16/10] lg:aspect-[4/3]">
        <img
          src={category.image}
          alt={`Seguro de ${category.name}`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/85 via-brand-ink/20 to-transparent" />
        <div className="absolute top-3 left-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-yellow text-white shadow-md">
          <Icon className="h-5 w-5" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-white/85">
            {category.tag}
          </p>
          <h3 className="font-display mt-1 text-base sm:text-lg font-bold text-white leading-tight">
            {category.name}
          </h3>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <p className="text-sm leading-relaxed text-gray-700">{category.desc}</p>
        <div className="mt-auto flex items-center gap-2 pt-4 text-xs font-semibold text-brand-ink">
          <ShieldCheck className="h-4 w-4 shrink-0 text-brand-gold" />
          {category.footer}
        </div>
      </div>
    </motion.div>
  );
};

const FeaturedCard = () => {
  const Icon = FEATURED.Icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`product-card-${FEATURED.id}`}
      className="group relative flex flex-col overflow-hidden rounded-3xl border-2 border-brand-yellow/60 bg-white shadow-sm ring-1 ring-brand-yellow/20 transition-all hover:shadow-xl hover:shadow-brand-ink/10 sm:col-span-2 sm:flex-row lg:col-span-3"
    >
      <div className="relative w-full shrink-0 overflow-hidden aspect-[16/10] sm:aspect-auto sm:w-[46%]">
        <img
          src={FEATURED.image}
          alt="Seguros para colaboradores de empresas industriales"
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/45 via-transparent to-transparent sm:bg-gradient-to-r sm:from-brand-ink/25 sm:via-transparent sm:to-transparent" />
        <div className="absolute top-3 left-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-yellow text-white shadow-md">
          <Icon className="h-5 w-5" />
        </div>
      </div>

      <div className="flex flex-1 flex-col justify-center p-5 sm:p-8">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-gold">
          {FEATURED.tag}
        </p>
        <h3 className="font-display mt-2 text-xl sm:text-2xl lg:text-3xl font-bold leading-tight text-brand-ink">
          {FEATURED.name}
        </h3>
        <p className="mt-3 max-w-2xl text-sm sm:text-base leading-relaxed text-gray-700">
          {FEATURED.desc}
        </p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {FEATURED.highlights.map((h) => (
            <span
              key={h}
              className="rounded-full bg-brand-yellow/20 px-2.5 py-1 text-[11.5px] font-semibold text-brand-ink"
            >
              {h}
            </span>
          ))}
        </div>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={() => scrollTo("cotizar")}
            data-testid="product-featured-cta"
            className="group/btn inline-flex h-12 items-center justify-center gap-2 rounded-full bg-brand-ink px-6 text-sm font-semibold text-white shadow-lg shadow-brand-ink/25 transition-all hover:-translate-y-0.5 hover:bg-brand-inkHover"
          >
            Cotizar el beneficio
            <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5" />
          </button>
          <p className="flex items-center gap-2 text-xs font-semibold text-brand-ink">
            <ShieldCheck className="h-4 w-4 shrink-0 text-brand-gold" />
            Sin costo para la empresa
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export const ProductShowcase = () => {
  return (
    <section id="productos" className="scroll-mt-24 py-12 sm:scroll-mt-28 sm:py-16">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-brand-ink">
            Protegemos todo lo que mueve tu operación industrial.
          </h2>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-gray-600">
            Seguros especializados para empresas industriales: desde la planta, la maquinaria
            y los equipos hasta la flota, la carga, los contratos y las personas. Una sola
            corredora para toda tu operación.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:mt-12 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
          {CATEGORIES.map((cat, i) => (
            <CategoryCard key={cat.id} category={cat} index={i} />
          ))}

          {/* Tarjeta CTA — completa la grilla de 9 celdas */}
          <div
            data-testid="product-advice-cta"
            className="flex flex-col justify-center gap-4 rounded-3xl border-2 border-dashed border-brand-yellow/60 bg-brand-yellow/10 p-5 sm:p-6"
          >
            <div>
              <p className="font-display text-lg font-bold text-brand-ink">
                ¿No sabes qué seguro necesitas?
              </p>
              <p className="mt-2 text-sm text-gray-700">
                Cuéntanos cómo opera tu empresa y armamos la combinación de coberturas que
                mejor te protege, sin costo.
              </p>
            </div>
            <button
              type="button"
              onClick={() => scrollTo("cotizar")}
              data-testid="product-advice-cta-btn"
              className="group inline-flex h-12 items-center justify-center gap-2 rounded-full bg-brand-ink px-6 text-sm font-semibold text-white shadow-lg shadow-brand-ink/25 transition-all hover:-translate-y-0.5 hover:bg-brand-inkHover sm:self-start"
            >
              Quiero asesoría
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>

          <FeaturedCard />
        </div>
      </div>
    </section>
  );
};
